import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { faqPageData } from "@/mocks/homeData";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";
import { FaqPageJsonLd, FaqBreadcrumbJsonLd } from "@/components/feature/JsonLd";

const DEFAULT_TITLE = "千華駐科技出版有限公司｜青城十九俠、自然科普與奇觀解謎";
const DEFAULT_DESC =
  "千華駐科技以青城十九俠系列與大華文苑書系為主要出版方向，網站提供品牌介紹、書系資訊與最新發行書籍。";
const FAQ_TITLE = "千華駐科技出版有限公司常見問題｜青城十九俠系列、大華文苑書系與購書資訊";
const FAQ_DESC =
  "查看千華駐科技出版有限公司常見問題，了解青城十九俠系列、大華文苑書系的正式書目查詢、購書通路及品牌資訊。";

function useFAQPageSEO() {
  const location = useLocation();

  useEffect(() => {
    const isFAQ = location.pathname === "/faq";

    if (!isFAQ) return;

    document.title = FAQ_TITLE;

    let metaDesc: HTMLMetaElement | null = document.querySelector("meta[name='description']");
    const originalDesc = metaDesc ? metaDesc.getAttribute("content") : DEFAULT_DESC;
    if (metaDesc) {
      metaDesc.setAttribute("content", FAQ_DESC);
    }

    return () => {
      document.title = DEFAULT_TITLE;
      if (metaDesc) {
        metaDesc.setAttribute("content", originalDesc || DEFAULT_DESC);
      }
    };
  }, [location.pathname]);
}

function getAllFaqs() {
  const flat: { q: string; a: string }[] = [];
  faqPageData.categories.forEach((cat) => {
    cat.faqs.forEach((faq) => {
      flat.push({ q: faq.q, a: faq.a });
    });
  });
  return flat;
}

function FAQHero() {
  const { hero } = faqPageData;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const introItems = [
    {
      icon: "ri-questionnaire-line",
      color: "text-accent-600",
      bg: "bg-accent-100",
    },
    {
      icon: "ri-book-3-line",
      color: "text-primary-600",
      bg: "bg-primary-100",
    },
    {
      icon: "ri-store-3-line",
      color: "text-secondary-600",
      bg: "bg-secondary-100",
    },
  ];

  return (
    <section
      className="relative overflow-hidden flex items-center justify-center"
      style={{ minHeight: "100svh" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Warm%20and%20welcoming%20abstract%20illustration%20for%20a%20help%20center%20or%20FAQ%20page%20of%20a%20cultural%20publishing%20brand%2C%20soft%20concentric%20ripples%20spreading%20outward%20like%20gentle%20answers%20unfolding%2C%20floating%20open%20book%20pages%20arranged%20in%20a%20harmonious%20spiral%20formation%20emitting%20warm%20amber%20glow%2C%20pale%20ivory%20and%20cream%20background%20with%20delicate%20warm%20gray%20linework%2C%20tiny%20sparkling%20particles%20of%20golden%20light%20scattered%20throughout%20the%20space%2C%20editorial%20art%20style%20with%20organic%20flowing%20shapes%2C%20no%20photographic%20realism%2C%20bright%20clear%20atmosphere%20with%20plenty%20of%20negative%20space%20for%20dark%20text%20overlay%2C%20calm%20helpful%20and%20inviting%20mood&width=1600&height=900&seq=qh-faq-brand-02&orientation=landscape&nocache=true')",
        }}
      />
      <div className="absolute inset-0 bg-white/20" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/50 via-black/15 to-transparent" />
      <div className="relative z-10 text-left px-4 w-full max-w-5xl mx-auto">
        <h1
          className={`font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-wide transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          {hero.title}
        </h1>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {hero.intro.map((text, i) => (
            <div
              key={i}
              className={`bg-background-50/90 backdrop-blur-sm border border-background-200/70 rounded-lg p-5 md:p-6 flex items-start gap-4 transition-all duration-[600ms] ease-out ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${200 + i * 120}ms` }}
            >
              <div
                className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full ${introItems[i].bg}`}
              >
                <i
                  className={`${introItems[i].icon} ${introItems[i].color} text-base`}
                ></i>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQListSection() {
  const { categories } = faqPageData;
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.05, sectionIndex: 0 });

  const allFaqs = useMemo(() => {
    const flat: { q: string; a: string; isFirstInCategory: boolean; categoryTitle: string }[] = [];
    categories.forEach((cat) => {
      cat.faqs.forEach((faq, fi) => {
        flat.push({
          q: faq.q,
          a: faq.a,
          isFirstInCategory: fi === 0,
          categoryTitle: cat.title,
        });
      });
    });
    return flat;
  }, [categories]);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  const handleKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle(i);
    }
  };

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-50"
      style={{ minHeight: "100svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div className="max-w-3xl mx-auto">
          {allFaqs.map((faq, i) => (
            <div key={i}>
              {faq.isFirstInCategory && (
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mt-10 mb-4 first:mt-0">
                  {faq.categoryTitle}
                </h2>
              )}
              <div
                className={`bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-[600ms] ease-out hover:border-foreground-200 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <h3 className="m-0">
                  <button
                    onClick={() => toggle(i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    id={`faq-question-${i}`}
                    aria-expanded={openIndex === i}
                    aria-controls={`faq-answer-${i}`}
                    className="w-full flex items-start gap-3 px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer focus:outline-2 focus:outline-offset-[-2px] focus:outline-accent-500"
                  >
                    <div
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5"
                      aria-hidden="true"
                    >
                      Q
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-heading text-sm md:text-base font-medium text-foreground-950 text-left block">
                          {faq.q}
                        </span>
                        <i
                          className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform duration-200 ${
                            openIndex === i ? "rotate-180" : ""
                          }`}
                          aria-hidden="true"
                        ></i>
                      </div>
                    </div>
                  </button>
                </h3>
                <div
                  id={`faq-answer-${i}`}
                  role="region"
                  aria-labelledby={`faq-question-${i}`}
                  className="overflow-hidden transition-all duration-300 motion-reduce:transition-none"
                  style={{
                    maxHeight: openIndex === i ? "2000px" : "0px",
                  }}
                >
                  <div className="px-5 pb-5 md:px-6 md:pb-6 flex items-start gap-3">
                    <div
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5"
                      aria-hidden="true"
                    >
                      A
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed text-left whitespace-pre-line">{faq.a}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQBottomCta() {
  const navigate = useNavigate();
  const { bottomCta } = faqPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.15, sectionIndex: 1 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-100"
      style={{ minHeight: "45svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div className="max-w-3xl mx-auto space-y-12 md:space-y-16">
          <div
            className={`text-center transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-wide mb-3">
              {bottomCta.catalog.title}
            </h2>
            <p className="text-sm text-foreground-600 mb-5 md:mb-6 max-w-xl mx-auto leading-relaxed">
              {bottomCta.catalog.description}
            </p>
            <a
              href={bottomCta.catalog.buttonHref}
              target="_blank"
              rel="nofollow noopener"
              className="inline-block px-6 py-3 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              {bottomCta.catalog.buttonLabel}
            </a>
          </div>
          <div
            className={`text-center transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "120ms" }}
          >
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-wide mb-3">
              {bottomCta.channels.title}
            </h2>
            <p className="text-sm text-foreground-600 mb-5 md:mb-6 max-w-xl mx-auto leading-relaxed">
              {bottomCta.channels.description}
            </p>
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(bottomCta.channels.buttonHref);
              }}
              className="px-6 py-3 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600"
            >
              {bottomCta.channels.buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FAQPage() {
  useFAQPageSEO();
  const allFaqs = getAllFaqs();

  return (
    <div className="bg-background-50">
      <FaqPageJsonLd faqs={allFaqs} />
      <FaqBreadcrumbJsonLd />
      <Navbar />
      <main>
        <FAQHero />
        <FAQListSection />
        <FAQBottomCta />
      </main>
      <Footer />
    </div>
  );
}